import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { resolveMedia, isPdfUrl, API } from "@/lib/api";
import { ArrowRight, CalendarDays, CalendarClock, CalendarPlus, FileText } from "lucide-react";
import useSEO from "@/lib/seo";

function formatDate(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString("de-DE", { day: "2-digit", month: "long", year: "numeric" }); } catch { return ""; }
}

function getNextTermin(items) {
  if (!items || items.length === 0) return null;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const upcoming = items
    .filter((t) => {
      const d = new Date(t.date); d.setHours(0, 0, 0, 0);
      return d >= today;
    })
    .sort((a, b) => new Date(a.date) - new Date(b.date));
  return upcoming[0] || null;
}

export default function NewsList() {
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [nextTermin, setNextTermin] = useState(null);
  useSEO({
    title: "Aktuelle News",
    description: "Aktuelle Nachrichten, Meldungen und Baufortschritte rund um das Quartier am Hornschen Tor in Detmold.",
    path: "/news",
  });

  useEffect(() => {
    api.get("/news", { params: { status: "published" } }).then((r) => setNews(r.data || [])).finally(() => setLoading(false));
    api.get("/termine", { params: { only_active: true } }).then((r) => setNextTermin(getNextTermin(r.data || []))).catch(() => setNextTermin(null));
  }, []);

  return (
    <div className="container-narrow py-16 lg:py-24" data-testid="news-list-page">
      <p className="text-xs uppercase tracking-[0.18em] text-[hsl(var(--brand-green-strong))] mb-4">Aktuelles</p>
      <h1 className="font-semibold text-4xl sm:text-5xl mb-4">Alle Nachrichten</h1>
      <p className="text-neutral-600 max-w-2xl mb-10">Aktuelle Informationen und Meldungen rund um das Quartier am Hornschen Tor.</p>

      {nextTermin && <NextTerminCard termin={nextTermin} />}

      {loading ? (
        <div className="text-neutral-500 text-sm">Lade…</div>
      ) : news.length === 0 ? (
        <div className="border border-dashed border-brand-line p-12 text-center text-neutral-500">
          Aktuell sind noch keine Meldungen veröffentlicht.
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-10">
          {news.map((n) => (
            <Link key={n.id} to={`/news/${n.slug}`} className="group block" data-testid={`news-list-item-${n.slug}`}>
              <div className="aspect-[4/3] overflow-hidden bg-neutral-100 mb-5 flex items-center justify-center relative">
                {n.image_url ? (
                  isPdfUrl(n.image_url) ? (
                    n.image_preview_url ? (
                      <>
                        <img src={resolveMedia(n.image_preview_url)} alt={n.title} className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-500" />
                        <span className="absolute top-3 left-3 bg-[hsl(var(--brand-green-dark))] text-white text-[10px] uppercase tracking-wider px-2 py-0.5 inline-flex items-center gap-1"><FileText className="h-3 w-3" />PDF</span>
                      </>
                    ) : (
                      <div className="flex flex-col items-center justify-center text-neutral-500 gap-2 px-4 text-center">
                        <FileText className="h-10 w-10" strokeWidth={1.5} />
                        <span className="text-[10px] uppercase tracking-wider">PDF-Dokument</span>
                      </div>
                    )
                  ) : (
                    <img src={resolveMedia(n.image_url)} alt={n.title} className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-500" />
                  )
                ) : (
                  <span className="text-neutral-300 text-xs">Kein Bild</span>
                )}
              </div>
              <p className="text-xs text-[hsl(var(--brand-green-strong))] flex items-center gap-2">
                <CalendarDays className="h-3.5 w-3.5" /> {formatDate(n.date)}
              </p>
              <h2 className="mt-2 font-semibold text-2xl leading-snug group-hover:text-[hsl(var(--brand-green-strong))] transition-colors">{n.title}</h2>
              {n.teaser && <p className="mt-2 text-sm text-neutral-600 line-clamp-3">{n.teaser}</p>}
              <span className="mt-4 inline-flex items-center gap-1 text-sm text-[hsl(var(--brand-green-strong))]">
                Mehr erfahren <ArrowRight className="h-3.5 w-3.5" />
              </span>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

function NextTerminCard({ termin }) {
  const d = new Date(termin.date);
  const day = d.toLocaleDateString("de-DE", { day: "2-digit" });
  const month = d.toLocaleDateString("de-DE", { month: "short" }).replace(".", "");
  const year = d.toLocaleDateString("de-DE", { year: "numeric" });
  return (
    <div
      data-testid="next-termin-card"
      className="mb-12 flex flex-col sm:flex-row sm:items-stretch gap-0 bg-[hsl(var(--brand-soft))] border border-[hsl(var(--brand-green))]/30 overflow-hidden"
    >
      <div className="flex sm:flex-col items-center justify-center gap-3 sm:gap-1 bg-[hsl(var(--brand-green-dark))] text-white px-6 sm:px-8 py-5 sm:py-6 sm:w-32 shrink-0">
        <span className="text-4xl sm:text-5xl font-semibold leading-none tracking-tight">{day}</span>
        <span className="text-xs sm:text-[11px] uppercase tracking-[0.16em] opacity-90">{month} {year}</span>
      </div>
      <div className="flex-1 px-6 sm:px-7 py-5 sm:py-6 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="text-[11px] uppercase tracking-[0.18em] text-[hsl(var(--brand-green-strong))] mb-1 inline-flex items-center gap-1.5">
            <CalendarClock className="h-3 w-3" /> Nächster Termin
          </p>
          <h2 className="font-semibold text-lg sm:text-xl text-neutral-900 leading-snug truncate">{termin.title}</h2>
        </div>
        <div className="flex flex-wrap items-center gap-x-5 gap-y-2 shrink-0">
          <Link
            to="/#termine"
            data-testid="next-termin-all"
            className="inline-flex items-center gap-1.5 text-sm font-medium text-[hsl(var(--brand-green-strong))] hover:underline"
          >
            Alle Termine <ArrowRight className="h-3.5 w-3.5" />
          </Link>
          <a
            href={`${API}/termine/${termin.id}/ics`}
            download
            data-testid="next-termin-ics"
            className="inline-flex items-center gap-1.5 text-sm font-medium text-[hsl(var(--brand-green-dark))] hover:text-[hsl(var(--brand-green-strong))]"
          >
            <CalendarPlus className="h-4 w-4" /> In Kalender
          </a>
        </div>
      </div>
    </div>
  );
}
